// ==========================================
// FILE: \src\screens\DetailScreen.js
// ==========================================

import React, { useState, useMemo } from 'react';
import { 
  View, Text, StyleSheet, TouchableOpacity, ScrollView, 
  Image, ActivityIndicator, Platform, Modal, TextInput, KeyboardAvoidingView
} from 'react-native';
import { useWineContext } from '../context/WineContext'; 
import { callGemini } from '../utils/geminiClient'; 
import EnergyHeader from '../components/EnergyHeader'; 
import { 
  ArrowLeft, Trash2, MinusCircle, PlusCircle, 
  MapPin, Star, Sparkles, BookOpen, Utensils, AlertCircle,
  Gem, Unlock, Info, RefreshCw, Pencil, X, Save,
  Map, LayoutGrid, Box, Wine as WineIcon
} from 'lucide-react-native';

import OutOfEnergyModal from '../components/OutOfEnergyModal';
import RewardedAdModal from '../components/RewardedAdModal';

export default function DetailScreen({ route, navigation }) {
  const { wine: initialWine } = route.params;
  
  const { 
    updateWineLocal, deleteWineLocal, consumeEnergy, addEnergy, 
    userProfile, locations, updateLocationLocal, t 
  } = useWineContext();
  
  const isPremium = userProfile?.isPremium || false;
  
  const [wine, setWine] = useState(initialWine);
  
  const [isEnriching, setIsEnriching] = useState(false);
  const [isCompletingData, setIsCompletingData] = useState(false);
  const [isUnlockingValue, setIsUnlockingValue] = useState(false);
  const [isRecalculatingWindow, setIsRecalculatingWindow] = useState(false); 
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false); 
  
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState(null);

  const [showVintagePrompt, setShowVintagePrompt] = useState(false);
  const [tempVintageInput, setTempVintageInput] = useState('');

  const [showAddModal, setShowAddModal] = useState(false);
  const [qtyToAdd, setQtyToAdd] = useState(1);
  const [showConsumeModal, setShowConsumeModal] = useState(false);

  const [showOutOfEnergy, setShowOutOfEnergy] = useState(false);
  const [showAdModal, setShowAdModal] = useState(false);

  const [customAlert, setCustomAlert] = useState({
    visible: false, title: '', message: '', type: 'info', onConfirm: null, confirmText: 'OK', cancelText: 'Annulla'
  });

  const isPast = wine.status === 'passato';
  const isDataIncomplete = !wine.annata || wine.estimatedValue === undefined;
  const isVintageChanged = editForm && editForm.annata !== wine.annata;

  // Rilevamento di disallineamento linguistico lato testi IA dinamici
  const needsTranslation = useMemo(() => {
    const dataLanguage = wine.generatedLanguage || 'it';
    const appLanguage = userProfile?.language || 'it';
    return dataLanguage !== appLanguage;
  }, [wine.generatedLanguage, userProfile?.language]);

  // Raccoglie tutti gli slot occupati da questo specifico vino
  const foundSlots = useMemo(() => {
    const slots = [];
    if (!locations) return slots;
    locations.forEach(loc => {
      if (loc.slots) {
        loc.slots.forEach(slot => {
          if (slot.wineId === wine.id) {
            slots.push({ locId: loc.id, locName: loc.name, ...slot });
          }
        });
      }
    });
    return slots;
  }, [locations, wine.id]);

  const orphanCount = Math.max(0, wine.quantity - foundSlots.length);

  const showAppAlert = (title, message, type = 'info', onConfirm = null, confirmText = t('global.ok'), cancelText = t('global.cancel')) => {
    setCustomAlert({ visible: true, title, message, type, onConfirm, confirmText, cancelText });
  };
  const closeAppAlert = () => setCustomAlert(prev => ({ ...prev, visible: false }));

  const handleAdComplete = () => {
    setShowAdModal(false);
    addEnergy(5);
    showAppAlert(t('addWine.alerts.adCompleteTitle'), t('addWine.alerts.adCompleteMsg'), "success");
  };

  const handleLocateBottle = async () => {
    if (!isPremium && userProfile?.energia < 1) {
      setShowOutOfEnergy(true);
      return;
    }

    const hasEnergy = await consumeEnergy(1);
    if (!hasEnergy) {
      setShowOutOfEnergy(true);
      return;
    }

    const targetLocId = foundSlots.length > 0 ? foundSlots[0].locId : null;
    navigation.navigate('Caveau', { highlightWineId: wine.id, targetLocId });
  };

  const handleLocateBottleFromConsume = () => {
    setShowConsumeModal(false);
    handleLocateBottle();
  };

  const handleOpenAddModal = () => {
    setQtyToAdd(1);
    setShowAddModal(true);
  };

  const confirmAddQuantity = async (placeInCaveau) => {
    setShowAddModal(false);
    const newQuantity = wine.quantity + qtyToAdd;
    const updatedWine = { ...wine, quantity: newQuantity, status: 'presente' };
    setWine(updatedWine);

    try {
      await updateWineLocal(wine.id, { quantity: newQuantity, status: 'presente' });
      if (placeInCaveau) {
        navigation.navigate('Caveau');
      } else {
        showAppAlert(t('details.alerts.addedTitle'), `${qtyToAdd} ${t('details.alerts.addedMsg')}`, "success");
      }
    } catch (error) {
      showAppAlert(t('global.error'), t('details.alerts.saveEditErrorMsg'), "error");
      setWine(wine); 
    }
  };

  const handleOpenConsumeModal = () => {
    if (foundSlots.length === 0) {
      executeConsumeBottle();
    } else {
      setShowConsumeModal(true);
    }
  };

  const executeConsumeBottle = async () => {
    if (!isPremium && userProfile?.energia < 1) {
      setShowConsumeModal(false);
      setShowOutOfEnergy(true);
      return;
    }

    const hasEnergy = await consumeEnergy(1);
    if (!hasEnergy) {
      setShowConsumeModal(false);
      setShowOutOfEnergy(true);
      return;
    }

    setShowConsumeModal(false);
    
    const newQuantity = Math.max(0, wine.quantity - 1);
    const newStatus = newQuantity === 0 ? 'passato' : wine.status;
    const updatedWine = { ...wine, quantity: newQuantity, status: newStatus };
    
    setWine(updatedWine);

    try {
      await updateWineLocal(wine.id, { quantity: newQuantity, status: newStatus });

      if (newQuantity === 0) {
        showAppAlert(t('details.alerts.bottleFinishedTitle'), t('details.alerts.bottleFinishedMsg'));
      } else {
        showAppAlert(t('details.alerts.cinCinTitle'), t('details.alerts.cinCinMsg'), "success");
      }
    } catch (error) {
      if (!isPremium) await addEnergy(1); 
      showAppAlert(t('global.error'), t('details.alerts.saveEditErrorMsg'), "error");
      setWine(wine); 
    }
  };

  const handleUnlockValue = async () => {
    if (isUnlockingValue) return;
    setIsUnlockingValue(true);

    if (!isPremium && userProfile?.energia < 1) {
      setShowOutOfEnergy(true);
      setIsUnlockingValue(false);
      return;
    }

    const hasEnergy = await consumeEnergy(1);
    if (!hasEnergy) {
      setShowOutOfEnergy(true);
      setIsUnlockingValue(false);
      return;
    }

    setTimeout(async () => {
      try {
        await updateWineLocal(wine.id, { valueUnlocked: true });
        setWine(prev => ({ ...prev, valueUnlocked: true }));
      } catch (error) {
        if (!isPremium) await addEnergy(1);
        showAppAlert(t('global.error'), t('profile.alerts.passwordErrorTitle'), "error");
      } finally {
        setIsUnlockingValue(false);
      }
    }, 2000);
  };

  const handleOpenEdit = () => {
    setEditForm({
      ...wine,
      technicalSheet: { ...(wine.technicalSheet || {}) },
      pairingTags: Array.isArray(wine.pairingTags) ? wine.pairingTags.join(', ') : (wine.pairingTags || '')
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    setIsSavingEdit(true);
    try {
      const updatedWineData = {
        ...editForm,
        pairingTags: typeof editForm.pairingTags === 'string' 
          ? editForm.pairingTags.split(',').map(t => t.trim()).filter(t => t) 
          : editForm.pairingTags
      };
      
      await updateWineLocal(wine.id, updatedWineData);
      setWine(updatedWineData);
      setShowEditModal(false);
      showAppAlert(t('global.success'), t('details.alerts.saveEditSuccessMsg'), "success");
    } catch (error) {
      showAppAlert(t('global.error'), t('details.alerts.saveEditErrorMsg'), "error");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const recalculateDrinkingWindow = async () => {
    if (isRecalculatingWindow) return;
    
    if (!editForm.annata || editForm.annata.trim() === '') {
      return showAppAlert(t('global.error'), t('details.alerts.emptyVintageMsg'), "info");
    }

    setIsRecalculatingWindow(true);
    try {
      const promptText = `Sei un Master Sommelier. Ho questo vino: "${editForm.name}" (Annata: ${editForm.annata}, Tipo: ${editForm.type}, Regione: ${editForm.technicalSheet?.regione || 'Sconosciuta'}, Vitigno: ${editForm.technicalSheet?.vitigno || 'Sconosciuto'}).
      Calcola la forchetta di consumo ideale (drinkingWindow) basandoti sull'annata indicata. Rispondi solo in formato JSON esatto: {"drinkingWindow": "2025-2032"}`;

      const extractedData = await callGemini(promptText, true);
      if (extractedData.drinkingWindow) {
        setEditForm(prev => ({ ...prev, drinkingWindow: extractedData.drinkingWindow }));
      }
    } catch (error) {
      showAppAlert(t('global.error'), t('details.alerts.recalcErrorMsg'), "error");
    } finally {
      setIsRecalculatingWindow(false);
    }
  };

  const handleTranslateSheet = async () => {
    if (isTranslating) return;
    setIsTranslating(true);

    if (!isPremium && userProfile?.energia < 1) {
      setShowOutOfEnergy(true);
      setIsTranslating(false);
      return;
    }

    const hasEnergy = await consumeEnergy(1);
    if (!hasEnergy) {
      setShowOutOfEnergy(true);
      setIsTranslating(false);
      return;
    }

    try {
      const langNames = { it: "Italiano", en: "Inglese (English)", fr: "Francese (Français)" };
      const targetLanguageName = langNames[userProfile?.language || 'it'] || "Italiano";

      const promptText = `Sei un Master Sommelier stellato. Traduci, adatta e riscrivi interamente i testi descrittivi di questo vino in lingua ${targetLanguageName}.
      Dati attuali della bottiglia:
      Nome: ${wine.name}
      Descrizione originale: ${wine.description || ''}
      Abbinamenti originali: ${wine.pairingTags ? (Array.isArray(wine.pairingTags) ? wine.pairingTags.join(', ') : wine.pairingTags) : ''}
      Nota profonda originale: ${wine.deepDetails || ''}

      REGOLE ASSOLUTE:
      1. Non fare una traduzione letterale scolastica, adatta i testi per renderli affascinanti ed enologicamente accurati in lingua ${targetLanguageName}.
      2. Mantieni immutati i dati tecnici rigidi.
      
      Rispondi SOLO ed ESCLUSIVAMENTE con un JSON valido con questa struttura esatta:
      {
        "description": "Nuova descrizione adattata in lingua ${targetLanguageName}",
        "pairingTags": "Tag abbinamento tradotti separati da virgola",
        "deepDetails": "Nuova nota profonda del sommelier adattata in lingua ${targetLanguageName}"
      }`;

      const extractedData = await callGemini(promptText, true);

      // --- INIZIO MODIFICA PARSING DIFENSIVO ---
      let parsedTags = [];
      if (extractedData.pairingTags) {
        if (typeof extractedData.pairingTags === 'string') {
          parsedTags = extractedData.pairingTags.split(',').map(tag => tag.trim()).filter(tag => tag);
        } else if (Array.isArray(extractedData.pairingTags)) {
          parsedTags = extractedData.pairingTags.map(tag => String(tag).trim()).filter(tag => tag);
        }
      } else {
        parsedTags = wine.pairingTags || [];
      }
      // --- FINE MODIFICA ---

      const updatedFields = {
        description: extractedData.description || wine.description,
        pairingTags: parsedTags,
        deepDetails: extractedData.deepDetails || wine.deepDetails,
        generatedLanguage: userProfile?.language || 'it'
      };

      await updateWineLocal(wine.id, updatedFields);
      setWine(prev => ({ ...prev, ...updatedFields }));
      showAppAlert(t('global.success'), t('details.alerts.saveEditSuccessMsg'), "success");

    } catch (error) {
      if (!isPremium) await addEnergy(1); 
      showAppAlert(t('global.error'), t('addWine.alerts.technicalErrorMsg'), "error");
    } finally {
      setIsTranslating(false);
    }
  };

  const handleDelete = () => {
    if (!wine || !wine.id) return;
    showAppAlert(
      t('details.alerts.deleteConfirmTitle'), t('details.alerts.deleteConfirmMsg'), "confirm",
      async () => {
        closeAppAlert(); 
        try {
          await deleteWineLocal(wine.id);
          navigation.goBack(); 
        } catch (error) { showAppAlert(t('global.error'), t('details.alerts.recalcErrorMsg'), "error"); }
      }, t('details.alerts.deleteActionBtn')
    );
  };

  const updateRating = async (stars) => {
    setWine({ ...wine, rating: stars });
    try { await updateWineLocal(wine.id, { rating: stars }); } catch (error) {}
  };

  const handleStartDataUpdate = () => {
    setTempVintageInput('');
    setShowVintagePrompt(true); 
  };

  const completeMissingDataWithVintage = async (finalVintage) => {
    if (isCompletingData) return;
    setShowVintagePrompt(false); 
    setIsCompletingData(true); 

    if (!isPremium && userProfile?.energia < 1) {
      setShowOutOfEnergy(true);
      setIsCompletingData(false);
      return;
    }

    const hasEnergy = await consumeEnergy(1);
    if (!hasEnergy) {
      setShowOutOfEnergy(true);
      setIsCompletingData(false);
      return;
    }
    
    try {
      const langNames = { it: "Italiano", en: "Inglese (English)", fr: "Francese (Français)" };
      const targetLanguageName = langNames[userProfile?.language || 'it'] || "Italiano";

      const promptText = `Sei un Master Sommelier. Ho questo vino nel database: "${wine.name}" (Tipo: ${wine.type}). L'utente indica che l'annata è: "${finalVintage}".
      Calcola i seguenti dati in lingua ${targetLanguageName}:
      1. Forchetta di consumo (drinkingWindow) basandoti sull'anno.
      2. Valore stimato di mercato in Euro (solo numero intero).
      Rispondi SOLO ed ESCLUSIVAMENTE con questo formato JSON esatto: {"drinkingWindow": "2025-2032", "estimatedValue": 25}`;

      const extractedData = await callGemini(promptText, true);

      const updatedData = { 
        annata: finalVintage,
        drinkingWindow: extractedData.drinkingWindow || wine.drinkingWindow,
        estimatedValue: parseInt(extractedData.estimatedValue) || 0,
        generatedLanguage: userProfile?.language || 'it'
      };

      await updateWineLocal(wine.id, updatedData);
      setWine(prev => ({ ...prev, ...updatedData }));
      showAppAlert(t('global.success'), t('details.alerts.saveEditSuccessMsg'), "success");

    } catch (error) {
      if (!isPremium) await addEnergy(1);
      showAppAlert(t('global.error'), t('addWine.alerts.technicalErrorMsg'), "error");
    } finally {
      setIsCompletingData(false);
    }
  };

  const enrichWineDetails = async () => {
    if (wine.deepDetails || isEnriching) return;
    setIsEnriching(true);

    if (!isPremium && userProfile?.energia < 1) {
      setShowOutOfEnergy(true);
      setIsEnriching(false);
      return;
    }

    const hasEnergy = await consumeEnergy(1);
    if (!hasEnergy) {
      setShowOutOfEnergy(true);
      setIsEnriching(false);
      return;
    }

    try {
      const langNames = { it: "Italiano", en: "Inglese (English)", fr: "Francese (Français)" };
      const targetLanguageName = langNames[userProfile?.language || 'it'] || "Italiano";

      const promptText = `Agisci come un Master Sommelier. Scrivi un approfondimento testuale affascinante (max 3 paragrafi) sul vino "${wine.name}" (Annata: ${wine.annata || 'S.A.'}, Tipologia: ${wine.type}). Rispondi interamente in lingua ${targetLanguageName}. Non usare formattazione complessa.`;
      
      const enrichedText = await callGemini(promptText, false);

      setWine(prev => ({ ...prev, deepDetails: enrichedText }));
      await updateWineLocal(wine.id, { deepDetails: enrichedText, generatedLanguage: userProfile?.language || 'it' });
    } catch (error) {
      if (!isPremium) await addEnergy(1);
      showAppAlert(t('global.error'), t('alerts.serverOverload'), "error");
    } finally {
      setIsEnriching(false);
    }
  };

  const translateTechKey = (key) => {
    const labelsMap = {
      vitigno: t('addWine.form.labels.vitigno'),
      regione: t('addWine.form.labels.regione'),
      gradazione: t('addWine.form.labels.gradazione'),
      temperatura: t('addWine.form.labels.temperatura'),
      affinamento: t('addWine.form.labels.affinamento')
    };
    return labelsMap[key.toLowerCase()] || key.charAt(0).toUpperCase() + key.slice(1);
  };

  return (
    <View style={styles.mainContainer}>
      <View style={styles.topRedBanner}>
         <EnergyHeader />
      </View>

      <View style={styles.pageHeaderCushion}>
        <View style={styles.headerLeft}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <ArrowLeft color="#0f172a" size={24} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('details.title')}</Text>
        </View>

        <View style={styles.headerActionsGroup}>
          <TouchableOpacity onPress={handleOpenEdit} style={styles.actionIconButton}>
            <Pencil color="#0f172a" size={20} />
          </TouchableOpacity>
          <TouchableOpacity onPress={handleDelete} style={styles.actionIconButtonDestructive}>
            <Trash2 color="#e11d48" size={20} />
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView style={styles.container} bounces={false} showsVerticalScrollIndicator={false}>
        
        <View style={[styles.heroSection, isPast ? styles.heroPast : styles.heroPresent]}>
          <View style={styles.heroContent}>
            {wine.image ? (
              <Image source={{ uri: wine.image }} style={styles.heroImage} resizeMode="cover" />
            ) : (
              <View style={styles.heroImagePlaceholder}>
                <Text style={{fontSize: 60}}>🍷</Text>
              </View>
            )}
            <Text style={styles.wineName}>{wine.name}</Text>
            <Text style={styles.wineVintage}>{t('addWine.form.labels.annata')}: {wine.annata || 'S.A.'}</Text>
            
            <View style={styles.badgesContainer}>
              <View style={styles.typeBadge}>
                <Text style={styles.typeBadgeText}>
                  {t(`addWine.form.types.${wine.type.toLowerCase() === 'bollicine' ? 'bollicine' : wine.type.toLowerCase() === 'rosato' ? 'rosato' : wine.type.toLowerCase() === 'dolce' ? 'dolce' : wine.type.toLowerCase() === 'bianco' ? 'bianco' : 'rosso'}`)}
                </Text>
              </View>
              {isPast ? (
                <View style={styles.statusBadgePassato}><Text style={styles.statusBadgeTextPassato}>{t('details.statusPast')}</Text></View>
              ) : (
                <View style={styles.statusBadgePresente}><Text style={styles.statusBadgeTextPresente}>{wine.quantity} {t('details.statusPresent')}</Text></View>
              )}
            </View>
          </View>
        </View>

        <View style={styles.controlsSection}>
          {!isPast ? (
            <View style={styles.quantityControls}>
              <TouchableOpacity onPress={handleOpenConsumeModal} style={styles.qButton}>
                <MinusCircle color="#e11d48" size={36} />
              </TouchableOpacity>
              <Text style={styles.quantityNumber}>{wine.quantity}</Text>
              <TouchableOpacity onPress={handleOpenAddModal} style={styles.qButton}>
                <PlusCircle color="#e11d48" size={36} />
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.ratingSection}>
              <Text style={styles.ratingTitle}>{t('details.valuation')}</Text>
              <View style={styles.starsRow}>
                {[1, 2, 3, 4, 5].map(star => (
                  <TouchableOpacity key={star} onPress={() => updateRating(star)} style={styles.starBtn}>
                    <Star size={36} color={wine.rating >= star ? "#facc15" : "#cbd5e1"} fill={wine.rating >= star ? "#facc15" : "transparent"} />
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          )}
        </View>

        <View style={styles.bodySection}>
          
          {/* BANNER DISALLINEAMENTO LINGUISTICO CON EFFETTO ANTI-SFASAMENTO */}
          {needsTranslation && !isDataIncomplete ? (
            <View style={styles.translateDataBanner}>
              <View style={styles.incompleteTextWrap}>
                <Sparkles color="#4f46e5" size={20} />
                <Text style={styles.translateTitle}>{t('details.translateBanner.title')}</Text>
              </View>
              <Text style={styles.translateDesc}>{t('details.translateBanner.desc')}</Text>
              <TouchableOpacity style={styles.translateBtn} onPress={handleTranslateSheet} disabled={isTranslating}>
                {isTranslating ? (
                  <ActivityIndicator color="#fff" size="small" />
                ) : (
                  <>
                    <RefreshCw color="#fff" size={16} style={{marginRight: 6}} />
                    <Text style={styles.translateBtnText}>{t('details.translateBanner.action')}</Text>
                  </>
                )}
              </TouchableOpacity>
            </View>
          ) : null}

          {isDataIncomplete ? (
            <View style={styles.incompleteDataBanner}>
              <View style={styles.incompleteTextWrap}>
                <AlertCircle color="#ea580c" size={20} />
                <Text style={styles.incompleteTitle}>{t('details.alignBanner.title') || t('global.alerts.missingDataTitle')}</Text>
              </View>
              <Text style={styles.incompleteDesc}>{t('details.alignBanner.desc')}</Text>
              <TouchableOpacity style={styles.incompleteBtn} onPress={handleStartDataUpdate} disabled={isCompletingData}>
                {isCompletingData ? (
                  <ActivityIndicator color="#fff" size="small" />
                ) : (
                  <>
                    <RefreshCw color="#fff" size={16} style={{marginRight: 6}} />
                    <Text style={styles.incompleteBtnText}>{t('details.alignBanner.action')}</Text>
                  </>
                )}
              </TouchableOpacity>
            </View>
          ) : null}

          <View style={styles.valueEstimationBox}>
            {isUnlockingValue ? (
              <View style={{flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 4}}>
                <ActivityIndicator color="#0ea5e9" size="small" style={{marginRight: 8}} />
                <Text style={styles.unlockValueText}>{t('details.valueBox.marketAnalysis')}</Text>
              </View>
            ) : !wine.valueUnlocked ? (
              <TouchableOpacity style={styles.unlockValueBtn} onPress={handleUnlockValue}>
                <Unlock color="#0ea5e9" size={18} style={{marginRight: 8}} />
                <Text style={styles.unlockValueText}>{t('details.valueBox.revealValue')}</Text>
              </TouchableOpacity>
            ) : (
              <View style={styles.unlockedValueContainer}>
                <View style={{flexDirection: 'row', alignItems: 'center'}}>
                  <Gem color="#0ea5e9" size={20} style={{marginRight: 8}} />
                  <Text style={styles.unlockedValueText}>
                    {t('details.valueBox.estimation')}: {(wine.estimatedValue && wine.estimatedValue > 0) ? `${wine.estimatedValue} €` : 'N/D'}
                  </Text>
                </View>
                <TouchableOpacity onPress={() => setShowDisclaimer(true)} style={styles.infoBtn}>
                  <Info color="#94a3b8" size={18} />
                </TouchableOpacity>
              </View>
            )}

            {foundSlots.length > 0 && (
              <View style={styles.localizationSection}>
                <View style={styles.localizationHeaderRow}>
                  <Text style={styles.localizationTitle}>{t('details.placement.title')}</Text>
                  <View style={styles.badgeCount}>
                    <Text style={styles.badgeCountText}>{foundSlots.length} {foundSlots.length === 1 ? t('details.placement.bottleSingle') : t('details.placement.bottlePlural')}</Text>
                  </View>
                </View>

                <TouchableOpacity style={styles.locateBtn} onPress={handleLocateBottle}>
                  <Map color="#475569" size={20} style={{marginRight: 8}} />
                  <Text style={styles.locateBtnText}>{t('details.placement.show3d')}</Text>
                  <View style={styles.locateActionWrap}>
                    <Text style={styles.locateActionText}>1 ⚡</Text>
                  </View>
                </TouchableOpacity>
              </View>
            )}
          </View>

          <View style={styles.sectionBlock}>
            <View style={styles.sectionHeader}><BookOpen color="#e11d48" size={20} /><Text style={styles.sectionTitle}>{t('details.sections.theWine')}</Text></View>
            <Text style={styles.descriptionText}>{wine.description}</Text>
          </View>

          {(wine.pairingTags && wine.pairingTags.length > 0) ? (
            <View style={styles.sectionBlock}>
              <View style={styles.sectionHeader}><Utensils color="#e11d48" size={20} /><Text style={styles.sectionTitle}>{t('details.sections.pairings')}</Text></View>
              <View style={styles.tagsContainer}>
                {wine.pairingTags.map((tag, idx) => <View key={idx} style={styles.tag}><Text style={styles.tagText}>{tag}</Text></View>)}
              </View>
            </View>
          ) : null}

          <View style={styles.aiSection}>
            <View style={styles.sectionHeader}><Sparkles color="#4f46e5" size={20} /><Text style={[styles.sectionTitle, {color: '#312e81'}]}>{t('details.sections.sommelierIa')}</Text></View>
            {!wine.deepDetails ? (
              <View style={styles.aiPromptContainer}>
                <Text style={styles.aiPromptText}>{t('details.sommelierPrompt.text')}</Text>
                <TouchableOpacity style={styles.aiButton} onPress={enrichWineDetails} disabled={isEnriching}>
                  {isEnriching ? <ActivityIndicator color="#fff" /> : <Text style={styles.aiButtonText}>{t('details.sommelierPrompt.action')}</Text>}
                </TouchableOpacity>
              </View>
            ) : <Text style={styles.aiResultText}>{wine.deepDetails}</Text>}
          </View>

          <View style={styles.techSheet}>
            <Text style={styles.techSheetTitle}>{t('details.sections.techSheet')}</Text>
            <View style={styles.techRow}><Text style={styles.techLabel}>{t('addWine.form.labels.drinkingWindow')}</Text><Text style={[styles.techValue, wine.isReadyToDrink ? {color: '#16a34a'} : null]}>{wine.drinkingWindow || 'N/D'}</Text></View>
            {wine.technicalSheet ? Object.entries(wine.technicalSheet).map(([chiave, valore]) => (
              <View key={chiave} style={styles.techRow}><Text style={styles.techLabel}>{translateTechKey(chiave)}</Text><Text style={styles.techValue}>{valore}</Text></View>
            )) : null}
          </View>

        </View>
      </ScrollView>

      {/* --- MODAL AGGIUNGI BOTTIGLIE (+) --- */}
      <Modal transparent={true} visible={showAddModal} animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalBox}>
            <Text style={styles.modalTitle}>{t('details.modals.addBottlesTitle')}</Text>
            <Text style={styles.modalMessage}>{t('details.modals.addBottlesMsg')}</Text>
            
            <View style={styles.modalQuantityControls}>
              <TouchableOpacity onPress={() => setQtyToAdd(Math.max(1, qtyToAdd - 1))} style={styles.qButtonModal}>
                <MinusCircle color="#0f172a" size={36} />
              </TouchableOpacity>
              <Text style={styles.quantityNumberModal}>{qtyToAdd}</Text>
              <TouchableOpacity onPress={() => setQtyToAdd(qtyToAdd + 1)} style={styles.qButtonModal}>
                <PlusCircle color="#0f172a" size={36} />
              </TouchableOpacity>
            </View>

            <View style={{ gap: 12, marginTop: 20 }}>
              <TouchableOpacity style={[styles.saveBtn, {backgroundColor: '#fff', borderWidth: 1, borderColor: '#cbd5e1'}]} onPress={() => confirmAddQuantity(false)}>
                <Text style={[styles.saveBtnText, {color: '#0f172a'}]}>{t('details.modals.saveOnlyList')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={() => confirmAddQuantity(true)}>
                <View style={{flexDirection: 'row', alignItems: 'center'}}>
                  <LayoutGrid color="#fff" size={20} style={{marginRight: 8}} />
                  <Text style={styles.saveBtnText}>{t('details.modals.placeNowCaveau')}</Text>
                </View>
              </TouchableOpacity>
              <TouchableOpacity style={{alignItems: 'center', padding: 10}} onPress={() => setShowAddModal(false)}>
                <Text style={{color: '#94a3b8', fontWeight: 'bold'}}>{t('global.cancel')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* --- MODAL CONSUMA BOTTIGLIE COMPATTO (-) --- */}
      <Modal transparent={true} visible={showConsumeModal} animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalBox}>
            <Text style={styles.modalTitle}>{t('details.modals.stappataTitle')}</Text>
            <Text style={styles.modalMessage}>{t('details.modals.stappataMsg')}</Text>
            
            <View style={{ gap: 12, marginVertical: 10 }}>
              {orphanCount > 0 && (
                <TouchableOpacity style={styles.locateBtn} onPress={() => executeConsumeBottle()}>
                  <Box color="#475569" size={20} style={{marginRight: 8}} />
                  <Text style={styles.locateBtnText}>{t('details.modals.unplacedInventory')}</Text>
                  <View style={styles.locateActionWrap}><Text style={styles.locateActionText}>1 ⚡</Text></View>
                </TouchableOpacity>
              )}
              {foundSlots.length > 0 && (
                <TouchableOpacity style={styles.locateBtn} onPress={handleLocateBottleFromConsume}>
                  <LayoutGrid color="#e11d48" size={20} style={{marginRight: 8}} />
                  <View style={{flex: 1}}>
                    <Text style={styles.locateBtnText}>{t('details.modals.from3dCellar')}</Text>
                  </View>
                  <View style={styles.locateActionWrap}><Text style={styles.locateActionText}>{t('details.modals.goAction')}</Text></View>
                </TouchableOpacity>
              )}
            </View>

            <TouchableOpacity style={{alignItems: 'center', padding: 10, marginTop: 10}} onPress={() => setShowConsumeModal(false)}>
              <Text style={{color: '#94a3b8', fontWeight: 'bold'}}>{t('details.modals.cancelOp')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* MODAL INSERIMENTO ANNATA */}
      <Modal transparent={true} visible={showVintagePrompt} animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalBox}>
            <View style={styles.modalHeader}>
              <View style={{backgroundColor: '#fff7ed', padding: 8, borderRadius: 12}}>
                <AlertCircle color="#ea580c" size={24} />
              </View>
              <Text style={styles.modalTitle}>{t('details.modals.insertVintageTitle')}</Text>
            </View>
            <Text style={styles.modalMessage}>{t('details.modals.insertVintageMsg')}</Text>
            <TextInput style={styles.vintageInput} placeholder={t('addWine.form.placeholders.vintageInput')} placeholderTextColor="#cbd5e1" keyboardType="number-pad" maxLength={4} value={tempVintageInput} onChangeText={setTempVintageInput} />
            <View style={[styles.modalButtons, {marginTop: 20}]}>
              <TouchableOpacity style={[styles.modalCancelBtn, {flex: 1}]} onPress={() => completeMissingDataWithVintage('S.A.')}>
                <Text style={[styles.modalCancelText, {fontSize: 14, textAlign: 'center'}]}>{t('addWine.buttons.unknownVintage')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.modalConfirmBtn, {flex: 1, backgroundColor: '#ea580c'}]} onPress={() => { const finalYear = tempVintageInput.trim().length === 4 ? tempVintageInput.trim() : 'S.A.'; completeMissingDataWithVintage(finalYear); }}>
                <Text style={styles.modalConfirmText}>{t('details.modals.confirmBtn')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* MODAL MODIFICA SCHEDA GENERALE */}
      <Modal visible={showEditModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowEditModal(false)}>
        <KeyboardAvoidingView style={styles.editModalContainer} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <View style={styles.editModalHeader}>
            <Text style={styles.editModalTitle}>{t('details.modals.editDataTitle')}</Text>
            <TouchableOpacity onPress={() => setShowEditModal(false)} style={styles.editModalCloseBtn}><X color="#0f172a" size={24} /></TouchableOpacity>
          </View>
          {editForm ? (
            <ScrollView style={styles.editModalScroll} contentContainerStyle={styles.editModalContent} showsVerticalScrollIndicator={false}>
              <Text style={styles.editInputLabel}>{t('addWine.form.labels.name')}</Text>
              <TextInput style={styles.editInput} value={editForm.name} onChangeText={t => setEditForm({...editForm, name: t})} />
              <View style={styles.editRow}>
                <View style={{flex: 1, marginRight: 10}}><Text style={styles.editInputLabel}>{t('addWine.form.labels.annata')}</Text><TextInput style={styles.editInput} value={editForm.annata} onChangeText={t => setEditForm({...editForm, annata: t})} placeholder={t('details.modals.vintageLabelHint')} /></View>
                <View style={{flex: 1}}><Text style={styles.editInputLabel}>{t('addWine.form.type')}</Text><TextInput style={styles.editInput} value={editForm.type} onChangeText={t => setEditForm({...editForm, type: t})} /></View>
              </View>
              <Text style={styles.editInputLabel}>{t('details.modals.storageLoc')}</Text>
              <TextInput style={styles.editInput} value={editForm.location} onChangeText={t => setEditForm({...editForm, location: t})} placeholder={t('details.modals.locationLabelHint')} />
              <Text style={[styles.editInputLabel, isVintageChanged ? {color: '#e11d48'} : null]}>{t('addWine.form.labels.drinkingWindow')}</Text>
              <View style={[styles.recalcInputContainer, isVintageChanged ? styles.recalcInputContainerHighlight : null]}>
                <TextInput style={styles.recalcInput} value={editForm.drinkingWindow} onChangeText={t => setEditForm({...editForm, drinkingWindow: t})} placeholder="Es: 2025-2032" />
                <TouchableOpacity style={[styles.recalcBtnInside, isRecalculatingWindow ? {opacity: 0.7} : null]} onPress={recalculateDrinkingWindow} disabled={isRecalculatingWindow}>
                  {isRecalculatingWindow ? <ActivityIndicator size="small" color="#fff" /> : <Text style={styles.recalcBtnText}>{t('details.modals.recalcAction')}</Text>}
                </TouchableOpacity>
              </View>
              {isVintageChanged ? <Text style={{fontSize: 12, color: '#e11d48', marginTop: 4, fontStyle: 'italic'}}>{t('details.modals.recalcHint')}</Text> : null}
              <Text style={styles.editInputLabel}>{t('addWine.form.labels.pairingTags')}</Text>
              <TextInput style={styles.editInput} value={editForm.pairingTags} onChangeText={t => setEditForm({...editForm, pairingTags: t})} placeholder={t('details.modals.pairingLabelHint')} />
              <Text style={styles.editSectionTitle}>{t('details.sections.techSheet')}</Text>
              <View style={styles.editRow}><View style={{flex: 1, marginRight: 10}}><Text style={styles.editInputLabel}>{t('addWine.form.labels.vitigno')}</Text><TextInput style={styles.editInput} value={editForm.technicalSheet?.vitigno} onChangeText={t => setEditForm({...editForm, technicalSheet: {...editForm.technicalSheet, vitigno: t}})} /></View><View style={{flex: 1}}><Text style={styles.editInputLabel}>{t('addWine.form.labels.regione')}</Text><TextInput style={styles.editInput} value={editForm.technicalSheet?.regione} onChangeText={t => setEditForm({...editForm, technicalSheet: {...editForm.technicalSheet, regione: t}})} /></View></View>
              <View style={styles.editRow}><View style={{flex: 1, marginRight: 10}}><Text style={styles.editInputLabel}>{t('addWine.form.labels.gradazione')}</Text><TextInput style={styles.editInput} value={editForm.technicalSheet?.gradazione} onChangeText={t => setEditForm({...editForm, technicalSheet: {...editForm.technicalSheet, gradazione: t}})} /></View><View style={{flex: 1}}><Text style={styles.editInputLabel}>{t('addWine.form.labels.temperatura')}</Text><TextInput style={styles.editInput} value={editForm.technicalSheet?.temperatura} onChangeText={t => setEditForm({...editForm, technicalSheet: {...editForm.technicalSheet, temperatura: t}})} /></View></View>
              <Text style={styles.editInputLabel}>{t('addWine.form.labels.affinamento')}</Text>
              <TextInput style={styles.editInput} value={editForm.technicalSheet?.affinamento} onChangeText={t => setEditForm({...editForm, technicalSheet: {...editForm.technicalSheet, affinamento: t}})} />
              <Text style={styles.editInputLabel}>{t('details.modals.descLabel')}</Text>
              <TextInput style={[styles.editInput, {height: 80}]} multiline value={editForm.description} onChangeText={t => setEditForm({...editForm, description: t})} />
            </ScrollView>
          ) : null}
          <View style={styles.editModalFooter}>
            <TouchableOpacity style={styles.editSaveBtn} onPress={handleSaveEdit} disabled={isSavingEdit}>
              {isSavingEdit ? <ActivityIndicator color="#fff" /> : <><Save color="#fff" size={20} style={{marginRight: 8}} /><Text style={styles.editSaveBtnText}>{t('details.modals.saveChanges')}</Text></>}
            </TouchableOpacity>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* MODAL DISCLAIMER */}
      <Modal transparent={true} visible={showDisclaimer} animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalBox}>
            <View style={styles.modalHeader}><Info color="#0ea5e9" size={28} /><Text style={styles.modalTitle}>{t('addWine.buttons.valueInfoTitle')}</Text></View>
            <Text style={styles.modalMessage}>{t('addWine.buttons.valueInfoMsg')}</Text>
            <View style={styles.modalButtons}><TouchableOpacity style={[styles.modalConfirmBtn, {backgroundColor: '#0f172a'}]} onPress={() => setShowDisclaimer(false)}><Text style={styles.modalConfirmText}>{t('login.buttons.dismiss')}</Text></TouchableOpacity></View>
          </View>
        </View>
      </Modal>

      {/* MODAL AVVISI DI SISTEMA */}
      <Modal transparent={true} visible={customAlert.visible} animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalBox}>
            <View style={styles.modalHeader}>
              {customAlert.type === 'error' ? <AlertCircle color="#ef4444" size={28} /> : customAlert.type === 'confirm' ? <Trash2 color="#ef4444" size={28} /> : <BookOpen color="#3b82f6" size={28} />}
              <Text style={styles.modalTitle}>{customAlert.title}</Text>
            </View>
            <Text style={styles.modalMessage}>{customAlert.message}</Text>
            <View style={styles.modalButtons}>
              {customAlert.type === 'confirm' ? <TouchableOpacity style={styles.modalCancelBtn} onPress={closeAppAlert}><Text style={styles.modalCancelText}>{customAlert.cancelText}</Text></TouchableOpacity> : null}
              <TouchableOpacity style={[styles.modalConfirmBtn, (customAlert.type === 'confirm' || customAlert.type === 'error') ? { backgroundColor: '#e11d48' } : { backgroundColor: '#0f172a' }]} onPress={() => { if (customAlert.onConfirm) customAlert.onConfirm(); else closeAppAlert(); }}><Text style={styles.modalConfirmText}>{customAlert.confirmText}</Text></TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <OutOfEnergyModal visible={showOutOfEnergy} onClose={() => setShowOutOfEnergy(false)} onWatchAd={() => { setShowOutOfEnergy(false); setShowAdModal(true); }} onSubscribe={() => { setShowOutOfEnergy(false); navigation.navigate('Paywall'); }} />
      <RewardedAdModal visible={showAdModal} onClose={() => setShowAdModal(false)} onReward={handleAdComplete} />

    </View>
  );
}

const styles = StyleSheet.create({
  mainContainer: { flex: 1, backgroundColor: '#f8fafc' }, 
  container: { flex: 1 },
  topRedBanner: { backgroundColor: '#e11d48', paddingBottom: 16, borderBottomLeftRadius: 24, borderBottomRightRadius: 24, elevation: 5, boxShadow: '0px 4px 6px rgba(225, 29, 72, 0.3)', zIndex: 50 },
  pageHeaderCushion: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'white', marginHorizontal: 16, marginTop: 16, padding: 16, borderRadius: 20, elevation: 3, boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.05)', zIndex: 10 },
  headerLeft: { flexDirection: 'row', alignItems: 'center' },
  backButton: { marginRight: 10, padding: 6, backgroundColor: '#f1f5f9', borderRadius: 10 },
  headerTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a' },
  headerActionsGroup: { flexDirection: 'row', gap: 10 },
  actionIconButton: { padding: 8, backgroundColor: '#f1f5f9', borderRadius: 12 },
  actionIconButtonDestructive: { padding: 8, backgroundColor: '#fff1f2', borderRadius: 12 },
  heroPresent: { backgroundColor: '#9f1239' }, 
  heroPast: { backgroundColor: '#1e293b' }, 
  heroSection: { marginTop: 16, marginHorizontal: 16, borderRadius: 24, padding: 24, elevation: 4, boxShadow: '0px 4px 5px rgba(0, 0, 0, 0.2)' },
  heroContent: { alignItems: 'center' }, 
  heroImage: { width: 140, height: 200, borderRadius: 16, borderWidth: 4, borderColor: 'rgba(255,255,255,0.2)' },
  heroImagePlaceholder: { width: 140, height: 200, borderRadius: 16, backgroundColor: 'rgba(255,255,255,0.1)', alignItems: 'center', justifyContent: 'center' },
  wineName: { fontSize: 26, fontWeight: 'bold', color: 'white', textAlign: 'center', marginTop: 16, marginBottom: 4 },
  wineVintage: { fontSize: 18, color: '#fca5a5', fontWeight: 'bold', marginBottom: 12 },
  badgesContainer: { flexDirection: 'row', gap: 8 }, 
  typeBadge: { backgroundColor: 'rgba(255,255,255,0.2)', paddingHorizontal: 12, paddingVertical: 4, borderRadius: 12 }, 
  typeBadgeText: { color: 'white', fontWeight: 'bold' },
  statusBadgePassato: { backgroundColor: 'rgba(255,255,255,0.1)', paddingHorizontal: 12, paddingVertical: 4, borderRadius: 12, borderWidth: 1, borderColor: '#94a3b8' }, 
  statusBadgeTextPassato: { color: '#e2e8f0', fontWeight: 'bold' },
  statusBadgePresente: { backgroundColor: 'rgba(22,163,74,0.3)', paddingHorizontal: 12, paddingVertical: 4, borderRadius: 12, borderWidth: 1, borderColor: '#4ade80' }, 
  statusBadgeTextPresente: { color: '#bbf7d0', fontWeight: 'bold' },
  controlsSection: { backgroundColor: 'white', marginHorizontal: 16, marginTop: 16, borderRadius: 20, padding: 20, elevation: 3, alignItems: 'center' },
  quantityControls: { flexDirection: 'row', alignItems: 'center', gap: 24 }, quantityNumber: { fontSize: 32, fontWeight: 'bold', color: '#0f172a' }, qButton: { padding: 4 },
  ratingSection: { alignItems: 'center' }, ratingTitle: { fontSize: 14, color: '#64748b', marginBottom: 8, fontWeight: '500' }, starsRow: { flexDirection: 'row', gap: 8 }, starBtn: { padding: 2 },
  bodySection: { padding: 24, paddingBottom: 60 },
  incompleteDataBanner: { backgroundColor: '#fff7ed', padding: 16, borderRadius: 16, marginBottom: 24, borderWidth: 1, borderColor: '#fed7aa' },
  incompleteTextWrap: { flexDirection: 'row', alignItems: 'center', marginBottom: 6 },
  incompleteTitle: { fontSize: 16, fontWeight: 'bold', color: '#9a3412', marginLeft: 8 },
  incompleteDesc: { fontSize: 14, color: '#c2410c', marginBottom: 12, lineHeight: 20 },
  incompleteBtn: { backgroundColor: '#ea580c', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 10, borderRadius: 12 },
  incompleteBtnText: { color: 'white', fontWeight: 'bold', fontSize: 14 },
  translateDataBanner: { backgroundColor: '#f5f3ff', padding: 16, borderRadius: 16, marginBottom: 24, borderWidth: 1, borderColor: '#ddd6fe' },
  translateTitle: { fontSize: 16, fontWeight: 'bold', color: '#5b21b6', marginLeft: 8 },
  translateDesc: { fontSize: 14, color: '#6d28d9', marginBottom: 12, lineHeight: 20 },
  translateBtn: { backgroundColor: '#6d28d9', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 12, borderRadius: 12, paddingHorizontal: 16 },
  translateBtnText: { color: 'white', fontWeight: 'bold', fontSize: 14 },
  valueEstimationBox: { backgroundColor: '#f0f9ff', padding: 16, borderRadius: 16, marginBottom: 24, borderWidth: 1, borderColor: '#bae6fd' },
  unlockValueBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 4 }, unlockValueText: { color: '#0ea5e9', fontWeight: 'bold', fontSize: 16 },
  unlockedValueContainer: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }, unlockedValueText: { fontSize: 18, fontWeight: 'bold', color: '#0369a1' }, infoBtn: { padding: 4 },
  localizationSection: { marginTop: 16, borderTopWidth: 1, borderTopColor: '#bae6fd', paddingTop: 16 },
  localizationHeaderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  localizationTitle: { fontSize: 13, fontWeight: 'bold', color: '#0369a1', textTransform: 'uppercase' },
  badgeCount: { backgroundColor: '#0284c7', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8 },
  badgeCountText: { color: 'white', fontSize: 12, fontWeight: 'bold' },
  locateBtn: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', padding: 12, borderRadius: 12, marginBottom: 8, borderWidth: 1, borderColor: '#e2e8f0' },
  locateBtnText: { flex: 1, color: '#0f172a', fontWeight: '600', fontSize: 14 },
  locateActionWrap: { backgroundColor: '#fff1f2', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 8 },
  locateActionText: { color: '#e11d48', fontSize: 12, fontWeight: 'bold' },
  sectionBlock: { marginBottom: 24 }, sectionHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 }, sectionTitle: { fontSize: 18, fontWeight: 'bold', color: '#0f172a', marginLeft: 8 }, descriptionText: { fontSize: 15, color: '#475569', lineHeight: 22 },
  tagsContainer: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 }, tag: { backgroundColor: '#fff1f2', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8, borderWidth: 1, borderColor: '#ffe4e6' }, tagText: { color: '#be123c', fontSize: 14, textTransform: 'capitalize' },
  aiSection: { backgroundColor: '#e0e7ff', padding: 20, borderRadius: 20, marginBottom: 24, borderWidth: 1, borderColor: '#c7d2fe' }, aiPromptContainer: { alignItems: 'center' }, aiPromptText: { textAlign: 'center', color: '#4338ca', marginBottom: 16, lineHeight: 20 }, aiButton: { backgroundColor: '#4f46e5', paddingVertical: 12, paddingHorizontal: 20, borderRadius: 12, width: '100%', alignItems: 'center' }, aiButtonText: { color: 'white', fontWeight: 'bold', fontSize: 16 }, aiResultText: { color: '#3730a3', fontSize: 15, lineHeight: 24 },
  techSheet: { backgroundColor: '#f1f5f9', padding: 20, borderRadius: 20, borderWidth: 1, borderColor: '#e2e8f0' }, techSheetTitle: { fontSize: 18, fontWeight: 'bold', color: '#0f172a', marginBottom: 16 }, techRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#e2e8f0' }, techLabel: { color: '#64748b', fontSize: 14 }, techValue: { color: '#0f172a', fontSize: 14, fontWeight: '600', maxWidth: '60%', textAlign: 'right' },
  vintageInput: { backgroundColor: '#f8fafc', borderWidth: 2, borderColor: '#e2e8f0', borderRadius: 12, padding: 16, fontSize: 24, color: '#0f172a', textAlign: 'center', fontWeight: 'bold', marginTop: 16 },
  editModalContainer: { flex: 1, backgroundColor: '#f8fafc' }, 
  editModalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 20, backgroundColor: 'white', borderBottomWidth: 1, borderBottomColor: '#e2e8f0', paddingTop: Platform.OS === 'ios' ? 50 : 20 }, 
  editModalTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a' }, 
  editModalCloseBtn: { padding: 6, backgroundColor: '#f1f5f9', borderRadius: 20 }, 
  editModalScroll: { flex: 1 }, 
  editModalContent: { padding: 20, paddingBottom: 40 }, 
  editInputLabel: { fontSize: 13, fontWeight: 'bold', color: '#475569', marginBottom: 6, marginTop: 12 }, 
  editInput: { backgroundColor: 'white', borderWidth: 1, borderColor: '#cbd5e1', borderRadius: 10, padding: 12, fontSize: 15, color: '#0f172a' }, 
  editRow: { flexDirection: 'row', justifyContent: 'space-between' }, 
  editSectionTitle: { fontSize: 18, fontWeight: 'bold', color: '#0f172a', marginTop: 24, marginBottom: 8, borderBottomWidth: 1, borderBottomColor: '#e2e8f0', paddingBottom: 6}, 
  editModalFooter: { padding: 20, backgroundColor: 'white', borderTopWidth: 1, borderTopColor: '#e2e8f0' }, 
  editSaveBtn: { flexDirection: 'row', justifyContent: 'center', alignItems: 'center', backgroundColor: '#e11d48', paddingVertical: 14, borderRadius: 12 }, 
  editSaveBtnText: { color: 'white', fontWeight: 'bold', fontSize: 16 },
  recalcInputContainer: { flexDirection: 'row', alignItems: 'center', backgroundColor: 'white', borderWidth: 1, borderColor: '#cbd5e1', borderRadius: 10, paddingRight: 4 },
  recalcInputContainerHighlight: { borderColor: '#e11d48', borderWidth: 2, backgroundColor: '#fff1f2' },
  recalcInput: { flex: 1, padding: 12, fontSize: 15, color: '#0f172a' },
  recalcBtnInside: { backgroundColor: '#e11d48', paddingVertical: 8, paddingHorizontal: 12, borderRadius: 8, marginLeft: 8 },
  recalcBtnText: { color: 'white', fontWeight: 'bold', fontSize: 13 },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(15, 23, 42, 0.6)', justifyContent: 'center', alignItems: 'center', padding: 20 }, 
  modalBox: { backgroundColor: 'white', borderRadius: 24, padding: 24, width: '100%', maxWidth: 400, boxShadow: '0px 10px 25px rgba(0,0,0,0.2)', elevation: 10 }, 
  modalHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 16 }, 
  modalTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a', marginLeft: 12, flex: 1 }, 
  modalMessage: { fontSize: 15, color: '#475569', lineHeight: 22, marginBottom: 24 }, 
  modalButtons: { flexDirection: 'row', justifyContent: 'flex-end', gap: 12 }, 
  modalCancelBtn: { paddingVertical: 12, paddingHorizontal: 20, borderRadius: 12, minWidth: 100, alignItems: 'center', backgroundColor: '#f1f5f9' }, 
  modalCancelText: { color: '#475569', fontWeight: 'bold', fontSize: 16 }, 
  modalConfirmBtn: { paddingVertical: 12, paddingHorizontal: 20, borderRadius: 12, minWidth: 100, alignItems: 'center' }, 
  modalConfirmText: { color: 'white', fontWeight: 'bold', fontSize: 16 },
  modalQuantityControls: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 24, marginVertical: 10 },
  qButtonModal: { padding: 4 },
  quantityNumberModal: { fontSize: 40, fontWeight: 'bold', color: '#0f172a' },
  saveBtn: { backgroundColor: '#e11d48', padding: 16, borderRadius: 14, alignItems: 'center' },
  saveBtnText: { color: '#fff', fontSize: 16, fontWeight: 'bold' }
});